import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress } from '@mui/material';

export default function Browser() {
  // Onglets
  const [tabs, setTabs] = useState([
    { id: 1, url: 'https://www.spidsoft.com', title: 'Spidsoft', history: ['https://www.spidsoft.com'], historyIndex: 0 }
  ]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [tabCounter, setTabCounter] = useState(2);
  // Favoris et historique global
  const [favorites, setFavorites] = useState(() => JSON.parse(localStorage.getItem('browserFavorites') || '[]'));
  const [globalHistory, setGlobalHistory] = useState(() => JSON.parse(localStorage.getItem('browserHistory') || '[]'));
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [proxyMode, setProxyMode] = useState(false); // Proxy actif par défaut
  const [iframeKey, setIframeKey] = useState(0); // Clé pour forcer le rechargement
  const [frameError, setFrameError] = useState(null); // Nouveau state pour les erreurs
  const [addressInput, setAddressInput] = useState(''); // Nouveau state pour la saisie temporaire
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Helpers
  const activeTab = tabs.find(t => t.id === activeTabId);
  const setActiveTabState = (patch) => {
    setTabs(tabs => tabs.map(tab => tab.id === activeTabId ? { ...tab, ...patch } : tab));
  };

  // Synchronise l'input avec l'URL actuelle
  useEffect(() => {
    setAddressInput(activeTab?.url || '');
  }, [activeTabId, activeTab?.url]);

  // Navigation
  const handleGo = (e) => {
    e.preventDefault();
    
    // Si c'est une recherche (pas une URL valide)
    if (!addressInput.match(/^https?:\/\//) && addressInput.trim()) {
      handleSearch();
      return;
    }
    
    // Sinon, traitement normal
    const url = addressInput.match(/^https?:\/\//) 
      ? addressInput 
      : `http://${addressInput}`;
      
    setIframeKey(k => k + 1);
    setActiveTabState({
      url: url,
      history: [...activeTab.history.slice(0, activeTab.historyIndex + 1), url],
      historyIndex: activeTab.history.length,
      title: url
    });
    setLoading(true);
  };
  const goBack = () => {
    if (activeTab.historyIndex > 0) {
      setActiveTabState(tab => ({
        ...tab,
        historyIndex: tab.historyIndex - 1,
        url: tab.history[tab.historyIndex - 1],
      }));
      setLoading(true);
    }
  };
  const goForward = () => {
    if (activeTab.historyIndex < activeTab.history.length - 1) {
      setActiveTabState(tab => ({
        ...tab,
        historyIndex: tab.historyIndex + 1,
        url: tab.history[tab.historyIndex + 1],
      }));
      setLoading(true);
    }
  };
  const refresh = () => {
    setLoading(true);
    setIframeKey(k => k + 1); // Force le rechargement
    setActiveTabState(tab => ({ ...tab, url: tab.history[tab.historyIndex] }));
  };
  const openInNewTab = () => {
    window.open(activeTab.url, '_blank');
  };
  // Onglets
  const addTab = (url = 'https://www.spidsoft.com') => {
    setTabs(tabs => [...tabs, { id: tabCounter, url, title: url, history: [url], historyIndex: 0 }]);
    setActiveTabId(tabCounter);
    setTabCounter(c => c + 1);
  };
  const closeTab = (id) => {
    let idx = tabs.findIndex(t => t.id === id);
    let newTabs = tabs.filter(t => t.id !== id);
    if (newTabs.length === 0) {
      addTab();
      return;
    }
    setTabs(newTabs);
    if (activeTabId === id) {
      setActiveTabId(newTabs[Math.max(0, idx - 1)].id);
    }
  };
  // Favoris
  const toggleFavorite = () => {
    if (favorites.includes(activeTab.url)) {
      const newFavs = favorites.filter(f => f !== activeTab.url);
      setFavorites(newFavs);
      localStorage.setItem('browserFavorites', JSON.stringify(newFavs));
    } else {
      const newFavs = [...favorites, activeTab.url];
      setFavorites(newFavs);
      localStorage.setItem('browserFavorites', JSON.stringify(newFavs));
    }
  };
  // Historique global
  useEffect(() => {
    if (!activeTab) return;
    if (!globalHistory.includes(activeTab.url)) {
      const newHist = [activeTab.url, ...globalHistory].slice(0, 100);
      setGlobalHistory(newHist);
      localStorage.setItem('browserHistory', JSON.stringify(newHist));
    }
  }, [activeTab && activeTab.url]);
  // Gestion du zoom
  const handleZoom = (delta) => setZoom(z => Math.max(0.2, Math.min(3, z + delta)));
  // Iframe loading
  const handleIframeLoad = () => {
    setLoading(false);
    setFrameError(null); // Réinitialise l'erreur si le chargement réussit
  };

  const handleIframeError = () => {
    setLoading(false);
    setFrameError("Ce site ne peut pas être affiché dans le navigateur (bloqué par X-Frame-Options)");
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddressInput(value);
    setSearchQuery(value);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const searchUrl = `https://search.spid.local/search?q=${encodeURIComponent(searchQuery)}`;
      setActiveTabState({ url: searchUrl });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Box p={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Onglets */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, display: 'flex', alignItems: 'center' }}>
        <Tabs value={tabs.findIndex(t => t.id === activeTabId)} onChange={(e, idx) => setActiveTabId(tabs[idx].id)} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab, idx) => (
            <Tab key={tab.id} label={tab.title || tab.url} sx={{ minWidth: 120 }} />
          ))}
        </Tabs>
        <Button size="small" sx={{ ml: 1 }} onClick={() => addTab()}>+</Button>
        {tabs.length > 1 && (
          <Button size="small" sx={{ ml: 1 }} color="error" onClick={() => closeTab(activeTabId)}>×</Button>
        )}
        <IconButton sx={{ ml: 2 }} onClick={() => setShowHistory(h => !h)} title="Historique"><HistoryIcon /></IconButton>
      </Box>
      {/* Barre d'adresse et favoris */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', width: '100%' }}>
        <IconButton onClick={goBack} disabled={activeTab.historyIndex === 0} size="small"><ArrowBackIcon /></IconButton>
        <IconButton onClick={goForward} disabled={activeTab.historyIndex === activeTab.history.length - 1} size="small"><ArrowForwardIcon /></IconButton>
        <IconButton onClick={refresh} size="small"><RefreshIcon /></IconButton>
        
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={addressInput}
          onChange={handleAddressChange}
          placeholder="URL ou recherche"
          onKeyPress={(e) => e.key === 'Enter' && handleGo(e)}
        />
        
        <IconButton 
          onClick={handleSearch}
          disabled={isSearching}
          title="Rechercher"
        >
          {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
        </IconButton>
        
        <IconButton onClick={toggleFavorite} size="small" title="Favoris">
          {favorites.includes(activeTab.url) ? <StarIcon color="warning" /> : <StarBorderIcon />}
        </IconButton>
        
        <label style={{display:'flex', alignItems:'center', marginLeft: '8px'}}>
          <input type="checkbox" checked={proxyMode} onChange={e => setProxyMode(e.target.checked)} style={{marginRight:4}} />
          Proxy
        </label>
        
        <IconButton onClick={openInNewTab} size="small" title="Ouvrir dans un nouvel onglet">
          <OpenInNewIcon />
        </IconButton>
        
        <IconButton onClick={() => handleZoom(0.1)} size="small" title="Zoom +">
          <ZoomInIcon />
        </IconButton>
        <IconButton onClick={() => handleZoom(-0.1)} size="small" title="Zoom -">
          <ZoomOutIcon />
        </IconButton>
      </Box>
      {/* Favoris rapides */}
      {favorites.length > 0 && (
        <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {favorites.map(fav => (
            <Button key={fav} size="small" variant="outlined" onClick={() => setActiveTabState({ url: fav })}>{fav}</Button>
          ))}
        </Box>
      )}
      {/* Affichage principal */}
      <Box sx={{
        flex: 1,
        border: '1px solid #ccc',
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0,
        display: 'flex'
      }}>

        {loading && <Box sx={{position:'absolute',top:0,left:0,right:0,bottom:0,display:'flex',alignItems:'center',justifyContent:'center',zIndex:2,background:'rgba(255,255,255,0.7)'}}><span>Chargement…</span></Box>}
        <iframe
          key={`${activeTab.url}-${iframeKey}`}
          title="fureteur-web"
          // On ne passe que url=... à l’API proxy, jamais d’autres params
          src={proxyMode && /^https?:\/\//.test(activeTab.url)
            ? (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/proxy-web?url=' + encodeURIComponent(activeTab.url.split('?')[0] + (activeTab.url.includes('?') ? '?' + activeTab.url.split('?')[1] : ''))
            : activeTab.url}
          width="100%"
          height="100%"
          style={{ border: 'none', flex: 1, transform: `scale(${zoom})`, transformOrigin: '0 0' }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
        {frameError && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            p: 2,
            textAlign: 'center'
          }}>
            <Typography color="error">
              {frameError}
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={() => window.open(activeTab.url, '_blank')}
                >
                  Ouvrir dans un nouvel onglet
                </Button>
              </Box>
            </Typography>
          </Box>
        )}
      </Box>
      {/* Historique global */}
      {showHistory && (
        <Box sx={{ position: 'absolute', top: 60, left: 40, zIndex: 100, background: '#fff', border: '1px solid #ccc', borderRadius: 2, p: 2, maxHeight: 300, overflow: 'auto', minWidth: 320 }}>
          <Typography variant="subtitle1">Historique</Typography>
          {globalHistory.length === 0 && <Typography variant="body2">Aucun historique</Typography>}
          {globalHistory.map((item, idx) => (
            <Box key={item + idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button size="small" variant="text" onClick={() => setActiveTabState({ url: item })}>{item}</Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
